import { useEffect, useState } from "react";
import {
  lookupMajorityDirection,
  majorityDirection,
  setupLine,
  sharedSetupStats,
  type MajorityDirection,
  type SetupStats,
  type SetupStatKey,
} from "../lib/setupStats";
import type { Setup } from "../types";

/**
 * The reveal's setup line: the Context's description, or null when the Round
 * carries no setup (a bank built before this feature).
 */
export function setupLineFor(setup: Setup | undefined): string | null {
  return setupLine(setup);
}

/**
 * The status bar's "always Higher"/"always Green" benchmark (GRA-151): the
 * whole-population majority direction for the active Loop's (timeframe,
 * preset), read from `setup-stats.json`.
 *
 * This fetches unconditionally on mount: the status bar is on screen before
 * any commit, so there is no "only after reveal" promise to keep here. `key`
 * is destructured to primitives for the effect's dependency list: a fresh
 * object literal every render would otherwise refetch (harmlessly, since
 * `sharedSetupStats` is cached, but needlessly) forever.
 */
export function useMajorityDirectionBaseline(
  key: SetupStatKey | null,
): MajorityDirection | null {
  const [stats, setStats] = useState<SetupStats | null>(null);
  const horizon = key?.horizon;
  const timeframe = key?.timeframe;
  const preset = key?.preset;

  useEffect(() => {
    if (!horizon) return;
    let live = true;
    void sharedSetupStats().then((loaded) => {
      if (live) setStats(loaded);
    });
    return () => {
      live = false;
    };
  }, [horizon, timeframe, preset]);

  if (!key) return null;
  return majorityDirection(lookupMajorityDirection(stats, key), key.horizon);
}
